import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useQuestionMappings, QuestionGroup } from "@/hooks/useQuestionMappings";
import { AvailableQuestions } from "@/pages/admin/components/question-mapping/AvailableQuestions";
import { GroupedQuestions } from "@/pages/admin/components/question-mapping/GroupedQuestions";
import { toast } from "sonner";
import {
  getAllQuestionsFromGroups,
  addQuestionsToPath,
  removeQuestionFromPath,
  deleteGroupAtPath,
  createGroupAtPath,
  renameGroupAtPath,
  migrateGroupsFormat,
} from "@/utils/questionGroupHelpers";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

interface QuestionGroupSectionProps {
  formId: string;
}

export function QuestionGroupSection({ formId }: QuestionGroupSectionProps) {
  const [groups, setGroups] = useState<QuestionGroup>({});
  const [hasChanges, setHasChanges] = useState(false);

  const {
    mapping,
    formQuestions,
    isLoading,
    saveMapping,
    isSaving,
  } = useQuestionMappings(formId);

  // Initialize groups when mapping is loaded
  useMemo(() => {
    if (mapping?.mapping_config?.groups) {
      const migratedGroups = migrateGroupsFormat(mapping.mapping_config.groups);
      console.log('🔍 MIGRATION DEBUG:');
      console.log('  - Original groups:', mapping.mapping_config.groups);
      console.log('  - Migrated groups:', migratedGroups);
      setGroups(migratedGroups);
      setHasChanges(true);
    } else if (formId) {
      setGroups({});
      setHasChanges(false);
    }
  }, [mapping, formId]);

  // Get available (ungrouped) questions
  const availableQuestions = useMemo(() => {
    const groupedQuestionNames = new Set(getAllQuestionsFromGroups(groups));
    console.log('🔍 QuestionGroupSection DEBUG:');
    console.log('  - Grouped questions:', Array.from(groupedQuestionNames));
    console.log('  - All form questions:', formQuestions.map(q => q.name));
    console.log('  - Groups structure:', groups);
    
    const filtered = formQuestions.filter(q => {
      const isGrouped = groupedQuestionNames.has(q.name);
      console.log(`  - Question ${q.name} is grouped: ${isGrouped}`);
      return !isGrouped;
    });
    console.log('  - Available questions after filter:', filtered.map(q => q.name));
    
    return filtered;
  }, [formQuestions, groups]);

  const handleCreateGroup = (path: string[], groupName: string) => {
    try {
      const updatedGroups = createGroupAtPath(groups, path, groupName);
      setGroups(updatedGroups);
      setHasChanges(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al crear grupo");
    }
  };

  const handleRenameGroup = (path: string[], newName: string) => {
    try {
      const updatedGroups = renameGroupAtPath(groups, path, newName);
      setGroups(updatedGroups);
      setHasChanges(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al renombrar grupo");
    }
  };

  const handleDeleteGroup = (path: string[]) => {
    const updatedGroups = deleteGroupAtPath(groups, path);
    setGroups(updatedGroups);
    setHasChanges(true);
  };

  const handleAddQuestionsToGroup = (path: string[], questionNames: string[]) => {
    const updatedGroups = addQuestionsToPath(groups, path, questionNames);
    setGroups(updatedGroups);
    setHasChanges(true);
  };

  const handleRemoveQuestionFromGroup = (path: string[], questionName: string) => {
    const updatedGroups = removeQuestionFromPath(groups, path, questionName);
    setGroups(updatedGroups);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMapping({ groups });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (formQuestions.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          No se encontraron preguntas en este formulario.
          <br />
          Asegúrate de que el formulario tenga al menos una submission.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="flex items-center gap-2"
        >
          <Save size={16} />
          {isSaving ? 'Guardando Grupos...' : 'Guardar Grupos'}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Columna Izquierda - Preguntas Disponibles */}
        <AvailableQuestions
          questions={availableQuestions}
          groups={groups}
          onAddToGroup={handleAddQuestionsToGroup}
        />

        {/* Columna Derecha - Grupos Configurados */}
        <GroupedQuestions
          groups={groups}
          formQuestions={formQuestions}
          onCreateGroup={handleCreateGroup}
          onRenameGroup={handleRenameGroup}
          onDeleteGroup={handleDeleteGroup}
          onRemoveQuestion={handleRemoveQuestionFromGroup}
        />
      </div>

      {hasChanges && (
        <div className="fixed bottom-6 right-6 bg-yellow-100 border-2 border-yellow-400 text-yellow-800 px-4 py-2 rounded-lg shadow-lg">
          Tienes cambios sin guardar en los grupos
        </div>
      )}
    </div>
  );
}
